import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FinanceiroService } from '../../../../../services/financeiro.service';
import { ParametrosService } from '../../../../../services/parametros.service';
import { CadastroService } from '../../../../../services/cadastro.service';
import {
  ContasAPagar,
  CriarContaAPagarParcelada,
  ParcelamentoContasAPagar,
  PeriodicidadeParcela
} from '../../../../../models/financeiro.model';
import { Fornecedor } from '../../../../../models/cadastro.model';
import { Grupo, Subgrupo } from '../../../../../models/parametros.model';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ErrorModel } from '../../../../../models/error.model';
import { nowLocalISOString } from '../../../../../core/utils/date.util';
import { formatarMoedaBRL } from '../../../../../core/utils/moeda.util';

@Component({
  selector: 'app-contas-a-pagar-manutencao',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './contas-a-pagar-manutencao.component.html',
  styleUrls: ['./contas-a-pagar-manutencao.component.css']
})
export class ContasAPagarManutencaoComponent implements OnInit {

  conta: ContasAPagar = this.getEmptyConta();
  isEditMode = false;
  fornecedores: Fornecedor[] = [];
  grupos: Grupo[] = [];
  subgrupos: Subgrupo[] = [];
  filteredSubgrupos: Subgrupo[] = [];
  valorAPagarDisplay = '';
  isReadOnly: boolean = true;
  isEdicaoBloqueada = false;

  // ----- Parcelamento (só no modo "nova conta") -----
  isParcelada = false;
  quantidadeParcelas = 2;
  periodicidade: PeriodicidadeParcela = 'MENSAL';
  dataPrimeiroVencimento = this.getTodayDateForInput();
  possuiEntrada = false;
  valorEntrada = 0;
  valorEntradaDisplay = '';
  dataEntrada = this.getTodayDateForInput();
  previa: ParcelamentoContasAPagar | null = null;
  isGerandoPrevia = false;

  readonly periodicidades: { valor: PeriodicidadeParcela; rotulo: string }[] = [
    { valor: 'MENSAL', rotulo: 'Mensal' },
    { valor: 'QUINZENAL', rotulo: 'Quinzenal' },
    { valor: 'SEMANAL', rotulo: 'Semanal' },
    { valor: 'ANUAL', rotulo: 'Anual' }
  ];


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private financeiroService: FinanceiroService,
    private parametrosService: ParametrosService,
    private cadastroService: CadastroService,
    private toastr: ToastrService
  ) { }

  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.loadDropdowns();
    this.syncValorAPagarDisplay();
  
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditMode = true;
      
      this.financeiroService.getContasAPagarById(+id).subscribe(data => {
        this.conta = data;
        this.syncSubgruposByGrupo();
        this.syncValorAPagarDisplay();
        this.isEdicaoBloqueada = (this.conta.status ?? '').toUpperCase() !== 'AGUARDANDO';
      });
    }
  }

  loadDropdowns(): void {
    this.cadastroService.getFornecedores().subscribe(data => this.fornecedores = data);
    this.parametrosService.getGrupos().subscribe(data => this.grupos = data);
    this.parametrosService.getSubgrupos().subscribe(data => {
      this.subgrupos = data;
      this.syncSubgruposByGrupo();
    });
  }

  onGrupoChange(): void {
    this.syncSubgruposByGrupo(true);
    this.invalidarPrevia();
  }

  onValorAPagarInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const rawDigits = input.value.replace(/\D/g, '');
    const parsedValue = Number(rawDigits || '0') / 100;

    this.conta.valor_a_pagar = parsedValue;
    this.valorAPagarDisplay = this.formatCurrency(parsedValue);
    input.value = this.valorAPagarDisplay;
    this.invalidarPrevia();
  }

  formatValorAPagarDisplay(): void {
    this.valorAPagarDisplay = this.conta.valor_a_pagar > 0
      ? this.formatCurrency(this.conta.valor_a_pagar)
      : '';
  }

  // ----- Parcelamento -----

  onValorEntradaInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const rawDigits = input.value.replace(/\D/g, '');
    const parsedValue = Number(rawDigits || '0') / 100;

    this.valorEntrada = parsedValue;
    this.valorEntradaDisplay = this.formatCurrency(parsedValue);
    input.value = this.valorEntradaDisplay;
    this.invalidarPrevia();
  }

  formatValorEntradaDisplay(): void {
    this.valorEntradaDisplay = this.valorEntrada > 0
      ? this.formatCurrency(this.valorEntrada)
      : '';
  }

  /** Qualquer alteração nos dados invalida a prévia já gerada. */
  invalidarPrevia(): void {
    this.previa = null;
  }

  gerarPrevia(): void {
    if (!this.validarDadosComuns()) {
      return;
    }

    this.isGerandoPrevia = true;
    this.financeiroService.simularParcelamento(this.montarPayloadParcelamento()).subscribe({
      next: (parcelamento) => {
        this.previa = parcelamento;
        this.isGerandoPrevia = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        const erro: ErrorModel = error.error;
        this.toastr.error(erro?.message ?? 'Não foi possível gerar a prévia das parcelas.');
        this.previa = null;
        this.isGerandoPrevia = false;
        this.cdr.detectChanges();
      }
    });
  }

  private montarPayloadParcelamento(): CriarContaAPagarParcelada {
    return {
      fornecedorId: Number(this.conta.fornecedor.id),
      grupoId: Number(this.conta.grupo.id),
      subgrupoId: Number(this.conta.subgrupo.id),
      descricao: this.conta.descricao,
      destino: this.conta.destino,
      observacao: this.conta.observacao,
      quantidadeParcelas: Number(this.quantidadeParcelas),
      valorParcela: this.conta.valor_a_pagar,
      dataPrimeiroVencimento: this.dataPrimeiroVencimento,
      periodicidade: this.periodicidade,
      possuiEntrada: this.possuiEntrada,
      valorEntrada: this.possuiEntrada ? this.valorEntrada : null,
      dataEntrada: this.possuiEntrada ? this.dataEntrada : null
    };
  }

  private validarDadosComuns(): boolean {
    if (Number(this.conta.fornecedor.id) <= 0) {
      this.toastr.warning('Selecione o Fornecedor.');
      return false;
    }

    if (!this.conta.descricao?.trim()) {
      this.toastr.warning('Informe a Descrição.');
      return false;
    }

    if (Number(this.conta.grupo.id) <= 0 || Number(this.conta.subgrupo.id) <= 0) {
      this.toastr.warning('Selecione Grupo de Despesa e Subgrupo de Despesa.');
      return false;
    }

    if (this.conta.valor_a_pagar <= 0) {
      this.toastr.warning('Informe o valor da parcela.');
      return false;
    }

    return true;
  }

  save(): void {
    if (this.isEdicaoBloqueada) {
      this.toastr.info('Somente contas com status AGUARDANDO podem ser editadas.');
      return;
    }

    if (Number(this.conta.grupo.id) <= 0 || Number(this.conta.subgrupo.id) <= 0) {
      this.toastr.warning('Selecione Grupo de Despesa e Subgrupo de Despesa.');
      return;
    }

    if (!this.isEditMode && this.isParcelada) {
      this.salvarParcelada();
      return;
    }

    if (this.isEditMode) {


      this.financeiroService.updateContasAPagar(this.conta).subscribe({
                next: () => {
                  this.navigateToListWithSuccessToast();
                },
                error: (error) => {
                  var erro: ErrorModel = error.error;
                  this.toastr.error(erro.message);
                }});
                 



    } else {
      this.financeiroService.createContasAPagar(this.conta).subscribe({
        next: () => {
          this.navigateToListWithSuccessToast();
        },
        error: (error) => {
          const erro: ErrorModel = error.error;
          this.toastr.error(erro.message);
        }
      });
    }
  }

  /** Só salva depois da prévia conferida — é ela que mostra ao usuário o que será gravado. */
  private salvarParcelada(): void {
    if (!this.previa) {
      this.toastr.warning('Gere a prévia das parcelas antes de salvar.');
      return;
    }

    const quantidade = this.previa.quantidadeParcelas;

    this.financeiroService.createContasAPagarParcelada(this.montarPayloadParcelamento()).subscribe({
      next: () => {
        this.router.navigate(['/home', 'financeiro-conta-a-pagar'], {
          state: {
            successMessage: `${quantidade} parcelas geradas com sucesso`
          }
        });
      },
      error: (error) => {
        const erro: ErrorModel = error.error;
        this.toastr.error(erro?.message ?? 'Não foi possível gerar as parcelas.');
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/home','financeiro-conta-a-pagar']);
  }

  getEmptyConta(): ContasAPagar {
    return {
      id: 0,
      fornecedor: { id: 0, razaoSocial: '', nomeFantasia: '', cnpj: '', inscricaoEstadual: '', inscricaoMunicipal: '', endereco: '', cidade: '', estado: '', cep: '', numeroPredial: '', telefone: '', email: '', site: '', observacoes: '' },
      descricao: '',
      valor_a_pagar: 0,
      valor_final: 0,
      dataVencimento: this.getTodayDateForInput(),
      dataPagamento: '',
      status: 'AGUARDANDO',
      destino: '',
      observacao: '',
      dataCadastro: nowLocalISOString(),
      grupo: { id: 0, nome: '', createdAt: '' },
      subgrupo: { id: 0, nome: '', createdAt: '', grupo: { id: 0, nome: '', createdAt: '' } },
      formaRetirada: ''
    };
  }

  private syncSubgruposByGrupo(resetSubgrupo: boolean = false): void {
    if (!this.subgrupos.length) {
      return;
    }

    const grupoId = Number(this.conta.grupo?.id ?? 0);

    if (grupoId <= 0) {
      this.filteredSubgrupos = [...this.subgrupos];
      if (resetSubgrupo) {
        this.conta.subgrupo.id = 0;
      }
      return;
    }

    this.filteredSubgrupos = this.subgrupos.filter(s => Number(s.grupo?.id) === grupoId);

    if (resetSubgrupo || !this.filteredSubgrupos.some(s => s.id === this.conta.subgrupo?.id)) {
      this.conta.subgrupo.id = 0;
    }
  }

  private syncValorAPagarDisplay(): void {
    this.valorAPagarDisplay = this.conta.valor_a_pagar > 0
      ? this.formatCurrency(this.conta.valor_a_pagar)
      : '';
  }

  private formatCurrency(value: number): string {
    return formatarMoedaBRL(value);
  }

  private getTodayDateForInput(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private navigateToListWithSuccessToast(): void {
    this.router.navigate(['/home', 'financeiro-conta-a-pagar'], {
      state: {
        successMessage: 'Nova conta a pagar registrada com sucesso'
      }
    });
  }
}
